import { db } from '../firebase';
import { doc, updateDoc, increment, collection, addDoc, getDoc, setDoc, runTransaction, serverTimestamp, query, where, getDocs, Timestamp, writeBatch } from 'firebase/firestore';
import { getUserSubscription } from './subscriptionService';

/**
 * Optimized credit service that reduces Firestore document count
 * while maintaining all analytics capabilities
 */

// Constants
const BATCH_SIZE = 50; // Number of usages to batch together
const USAGE_TYPES = {
  TEMPLATE: 'template',
  IMAGE: 'image',
  SIZECHART: 'sizechart',
  OTHER: 'other'
};

/**
 * Initialize user credits
 * @param {string} userId - User ID
 * @returns {Promise<boolean>} - Success status
 */
export const initializeUserCredits = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();

    if (!userData?.credits) {
      // Initialize credits if they don't exist
      await updateDoc(userRef, {
        credits: {
          total: 100, // Legacy field - keeping for backward compatibility
          used: 0,
          monthly: 100, // New field for monthly subscription credits
          flex: 0, // New field for flex credits
          lastRefillDate: new Date().toISOString()
        }
      });
      return true;
    }

    // Ensure all required fields exist
    const updates = {};
    if (userData.credits.used === undefined) {
      updates['credits.used'] = 0;
    }
    if (userData.credits.total === undefined) {
      updates['credits.total'] = 100;
    }
    if (userData.credits.monthly === undefined) {
      updates['credits.monthly'] = userData.credits.total || 100;
    }
    if (userData.credits.flex === undefined) {
      updates['credits.flex'] = 0;
    }
    if (userData.credits.lastRefillDate === undefined) {
      updates['credits.lastRefillDate'] = new Date().toISOString();
    }

    // Apply any missing fields
    if (Object.keys(updates).length > 0) {
      await updateDoc(userRef, updates);
    }

    return true;
  } catch (error) {
    console.error('Error initializing user credits:', error);
    throw error;
  }
};

/**
 * Record credit usage with batching to reduce document count
 * @param {string} userId - User ID
 * @param {number} creditsUsed - Number of credits used
 * @param {Object} metadata - Usage metadata
 * @returns {Promise<boolean>} - Success status
 */
export const recordCreditUsage = async (userId, creditsUsed = 1, metadata = {}) => {
  try {
    // Determine usage type
    let usageType = USAGE_TYPES.OTHER;
    if (metadata.type === 'sizechart_generation' || metadata.type === 'sizechart_comparison') {
      usageType = USAGE_TYPES.SIZECHART;
    } else if (metadata.templateId) {
      usageType = USAGE_TYPES.TEMPLATE;
    } else if (metadata.type === 'image_generation' || metadata.type === 'image_processing' || metadata.type === 'image') {
      usageType = USAGE_TYPES.IMAGE;
    }
    
    // Log the usage type for debugging
    console.log('Recording credit usage with type:', {
      providedType: metadata.type,
      mappedUsageType: usageType,
      credits: creditsUsed
    });

    // Get the current date for daily and monthly aggregation
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    const dateKey = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    const monthKey = `${year}-${month.toString().padStart(2, '0')}`;

    // Update user's credit aggregation document
    const userCreditStatsRef = doc(db, 'credit_usage_stats', userId);
    const userCreditStatsDoc = await getDoc(userCreditStatsRef);

    // Prepare updates for the aggregation document
    const updates = {
      [`total`]: increment(creditsUsed),
      [`byType.${usageType}`]: increment(creditsUsed),
      [`byDay.${dateKey}`]: increment(creditsUsed),
      [`byMonth.${monthKey}`]: increment(creditsUsed),
      [`byType.${usageType}.byDay.${dateKey}`]: increment(creditsUsed),
      [`byType.${usageType}.byMonth.${monthKey}`]: increment(creditsUsed),
      // Add type-specific data to daily and monthly breakdowns for easier access
      [`byDayType.${dateKey}.${usageType}`]: increment(creditsUsed),
      [`byMonthType.${monthKey}.${usageType}`]: increment(creditsUsed),
      lastUpdated: serverTimestamp()
    };

    // If shop-specific, also track by shop
    if (metadata.shopId) {
      updates[`byShop.${metadata.shopId}.total`] = increment(creditsUsed);
      updates[`byShop.${metadata.shopId}.byType.${usageType}`] = increment(creditsUsed);
      updates[`byShop.${metadata.shopId}.byDay.${dateKey}`] = increment(creditsUsed);
      updates[`byShop.${metadata.shopId}.byMonth.${monthKey}`] = increment(creditsUsed);
    }

    // Use setDoc with merge option to create or update the document
    await setDoc(userCreditStatsRef, updates, { merge: true });

    // For detailed analytics, we still need individual records but we can batch them
    // Get or create the current batch document
    const batchRef = doc(db, 'credit_usage_batches', `${userId}_${monthKey}`);
    const batchDoc = await getDoc(batchRef);

    // Create a new usage entry
    const usageEntry = {
      timestamp: Timestamp.now(),
      credits: creditsUsed,
      usageType,
      shopId: metadata.shopId || null,
      templateId: metadata.templateId || null,
      modelId: metadata.modelId || null,
      productId: metadata.productId || null,
      type: metadata.type || null
    };

    if (batchDoc.exists()) {
      // Add to existing batch
      const batch = batchDoc.data();
      const usages = batch.usages || [];
      
      // If batch is full, create a new one
      if (usages.length >= BATCH_SIZE) {
        // Create a new batch document with a timestamp suffix
        const newBatchRef = doc(db, 'credit_usage_batches', `${userId}_${monthKey}_${Date.now()}`);
        await setDoc(newBatchRef, {
          userId,
          monthKey,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          usages: [usageEntry]
        });
      } else {
        // Add to existing batch
        usages.push(usageEntry);
        await updateDoc(batchRef, {
          usages,
          updatedAt: serverTimestamp()
        });
      }
    } else {
      // Create first batch for this month
      await setDoc(batchRef, {
        userId,
        monthKey,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        usages: [usageEntry]
      });
    }

    // Update shop metrics if applicable
    if (metadata.shopId) {
      const shopRef = doc(db, 'shops', metadata.shopId);
      const shopUpdates = {
        'metrics.creditUsage.total': increment(creditsUsed),
        [`metrics.creditUsage.byType.${usageType}`]: increment(creditsUsed),
        [`metrics.creditUsage.byDay.${dateKey}`]: increment(creditsUsed),
        [`metrics.creditUsage.byMonth.${monthKey}`]: increment(creditsUsed),
        'metrics.lastActivity': serverTimestamp()
      };
      
      await updateDoc(shopRef, shopUpdates);
    }

    return true;
  } catch (error) {
    console.error('Error recording credit usage:', error);
    throw error;
  }
};

/**
 * Deduct credits from user's account
 * @param {string} userId - User ID
 * @param {number} creditsToDeduct - Number of credits to deduct
 * @param {Object} metadata - Usage metadata
 * @returns {Promise<boolean>} - Success status
 */
export const deductCredits = async (userId, creditsToDeduct = 1, metadata = {}) => {
  try {
    const userRef = doc(db, 'users', userId);
    
    // Use a transaction to ensure credit balance consistency
    await runTransaction(db, async (transaction) => {
      const userDoc = await transaction.get(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }
      
      const userData = userDoc.data();
      if (!userData.credits) {
        throw new Error('No credits found for user');
      }
      
      // Calculate credit deductions
      const monthlyCredits = userData.credits.monthly || 0;
      const flexCredits = userData.credits.flex || 0;
      const totalAvailableCredits = monthlyCredits + flexCredits;
      
      if (totalAvailableCredits < creditsToDeduct) {
        throw new Error(`Not enough credits. You have ${monthlyCredits} monthly credits and ${flexCredits} flex credits, but need ${creditsToDeduct} credits.`);
      }
      
      // Determine how many credits to deduct from each pool
      let monthlyToDeduct = Math.min(monthlyCredits, creditsToDeduct);
      let flexToDeduct = creditsToDeduct - monthlyToDeduct;
      
      // Prepare updates
      const updates = {
        'credits.used': increment(creditsToDeduct) // Increment total used credits
      };
      
      // Deduct from monthly credits first
      if (monthlyToDeduct > 0) {
        updates['credits.monthly'] = monthlyCredits - monthlyToDeduct;
        updates['credits.total'] = Math.max(0, (userData.credits.total || 0) - monthlyToDeduct); // For backward compatibility
      }
      
      // Then deduct from flex credits if needed
      if (flexToDeduct > 0) {
        updates['credits.flex'] = flexCredits - flexToDeduct;
      }
      
      // Apply the updates
      transaction.update(userRef, updates);
    });
    
    // Record the credit usage
    await recordCreditUsage(userId, creditsToDeduct, metadata);
    
    return true;
  } catch (error) {
    console.error('Error deducting credits:', error);
    throw error;
  }
};

/**
 * Get credit usage statistics
 * @param {string} userId - User ID
 * @param {number} monthsToFetch - Number of months to fetch
 * @returns {Promise<Object>} - Credit usage statistics
 */
export const getCreditUsageStats = async (userId, monthsToFetch = 6) => {
  try {
    console.log('Getting credit usage stats for user:', userId);
    
    // Get the aggregated stats document
    const statsRef = doc(db, 'credit_usage_stats', userId);
    const statsDoc = await getDoc(statsRef);
    
    // Log the raw stats document for debugging
    console.log('Raw credit usage stats document:', statsDoc.exists() ? 'exists' : 'does not exist');
    
    if (statsDoc.exists()) {
      const rawData = statsDoc.data();
      console.log('Credit stats raw data:', {
        total: rawData.total,
        byType: rawData.byType,
        byDay: Object.keys(rawData.byDay || {}).length + ' days',
        byMonth: Object.keys(rawData.byMonth || {}).length + ' months'
      });
    }
    
    if (!statsDoc.exists()) {
      return {
        totalUsage: 0,
        usageByType: { template: 0, image: 0, sizechart: 0, other: 0 },
        monthlyTrend: [],
        dailyTrend: [],
        usagePercentByType: { template: 0, image: 0, sizechart: 0, other: 0 }
      };
    }
    
    const stats = statsDoc.data();
    
    // Get current date and calculate date range
    const now = new Date();
    const months = [];
    const days = [];
    
    // Generate month keys for the last X months
    for (let i = 0; i < monthsToFetch; i++) {
      const date = new Date(now);
      date.setMonth(now.getMonth() - i);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months.unshift(monthKey); // Add to beginning to maintain chronological order
    }
    
    // Generate day keys for the last 14 days
    for (let i = 0; i < 14; i++) {
      const date = new Date(now);
      date.setDate(now.getDate() - (13 - i));
      const dayKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      days.push(dayKey);
    }
    
    // Extract usage by type directly from the Firestore document structure
    const usageByType = {
      template: 0,
      image: 0,
      sizechart: 0,
      other: 0
    };
    
    console.log('Raw credit stats data for extraction:', stats);
    
    // We'll use a prioritized approach to avoid double-counting:
    // 1. First try to get direct byType fields from the root
    // 2. If not found, check byShop structure
    // 3. If neither is found, fall back to other methods
    
    let foundDirectByType = false;
    
    // Check for byType fields directly in the document root
    // These fields might be named like 'byType.image', 'byType.sizechart', etc.
    for (const key in stats) {
      if (key.startsWith('byType.') && !key.includes('.byDay.') && !key.includes('.byMonth.')) {
        const type = key.split('.')[1]; // Extract the type (image, sizechart, etc.)
        if (type in usageByType) {
          usageByType[type] = stats[key] || 0;
          foundDirectByType = true;
        }
      }
    }
    
    // Handle different data structures using a prioritized approach to avoid double-counting
    // Only proceed to the next method if we haven't found data in the previous method
    
    // Method 1: Direct byType fields in the document root (already processed above)
    
    // Method 2: If we haven't found direct byType fields, check if byType is an object
    if (!foundDirectByType && stats.byType) {
      // Check if byType contains direct counts
      if (typeof stats.byType.template === 'number' || typeof stats.byType.image === 'number' || 
          typeof stats.byType.sizechart === 'number' || typeof stats.byType.other === 'number') {
        usageByType.template = stats.byType.template || 0;
        usageByType.image = stats.byType.image || 0;
        usageByType.sizechart = stats.byType.sizechart || 0;
        usageByType.other = stats.byType.other || 0;
        foundDirectByType = true;
      } 
      // Check if byType contains nested objects
      else {
        let foundNestedData = false;
        
        // Sum up the monthly values for each type if available
        if (stats.byType.template && stats.byType.template.byMonth) {
          const templateMonthlyValues = Object.values(stats.byType.template.byMonth || {});
          usageByType.template = templateMonthlyValues.reduce((sum, val) => sum + val, 0);
          foundNestedData = true;
        }
        
        if (stats.byType.image && stats.byType.image.byMonth) {
          const imageMonthlyValues = Object.values(stats.byType.image.byMonth || {});
          usageByType.image = imageMonthlyValues.reduce((sum, val) => sum + val, 0);
          foundNestedData = true;
        }
        
        if (stats.byType.sizechart && stats.byType.sizechart.byMonth) {
          const sizechartMonthlyValues = Object.values(stats.byType.sizechart.byMonth || {});
          usageByType.sizechart = sizechartMonthlyValues.reduce((sum, val) => sum + val, 0);
          foundNestedData = true;
        }
        
        if (stats.byType.other && stats.byType.other.byMonth) {
          const otherMonthlyValues = Object.values(stats.byType.other.byMonth || {});
          usageByType.other = otherMonthlyValues.reduce((sum, val) => sum + val, 0);
          foundNestedData = true;
        }
        
        if (foundNestedData) {
          foundDirectByType = true;
        }
      }
    }
    
    // Method 3: If we still haven't found data, check byShop structure
    if (!foundDirectByType && stats.byShop) {
      console.log('No direct byType fields found, checking byShop structure:', stats.byShop);
      let foundShopData = false;
      
      // Iterate through all shops and sum up the credits by type
      Object.entries(stats.byShop).forEach(([shopId, shopStats]) => {
        console.log(`Processing shop ${shopId}:`, shopStats);
        
        // Check for byType fields in the shop data
        if (shopStats.byType) {
          console.log('Shop byType data:', shopStats.byType);
          usageByType.template += shopStats.byType.template || 0;
          usageByType.image += shopStats.byType.image || 0;
          usageByType.sizechart += shopStats.byType.sizechart || 0;
          usageByType.other += shopStats.byType.other || 0;
          foundShopData = true;
        }
        
        // Only check for byType.* fields if we haven't found byType object
        if (!foundShopData) {
          for (const key in shopStats) {
            if (key.startsWith('byType.') && !key.includes('.byDay.') && !key.includes('.byMonth.')) {
              const type = key.split('.')[1]; // Extract the type (image, sizechart, etc.)
              if (type in usageByType) {
                console.log(`Found ${type} in shop ${shopId}:`, shopStats[key]);
                usageByType[type] += shopStats[key] || 0;
                foundShopData = true;
              }
            }
          }
        }
      });
      
      if (foundShopData) {
        foundDirectByType = true;
      }
      
      console.log('After processing byShop, usageByType:', usageByType);
    }
    
    // Check for byType.* fields in the document root
    console.log('Final usageByType after all processing:', usageByType);
    
    // If byType is undefined but we have direct byType.* fields, we've already processed them
    // Otherwise, check if we can determine the type from other data in the document
    if (!foundDirectByType && stats.total) {
      // If we have byType.* fields in the document, we've already processed them above
      // Check if we have any direct byType.* fields that we've already processed
      const hasProcessedDirectFields = usageByType.template > 0 || usageByType.image > 0 || 
                                     usageByType.sizechart > 0 || usageByType.other > 0;
      
      if (!hasProcessedDirectFields) {
        // For backward compatibility with old data structure
        // Only do this if we haven't found any type-specific data
        console.log('No type-specific data found, using total for backward compatibility');
        usageByType.image = stats.total;
      }
    }
    
    // Log the usage by type for debugging
    console.log('Credit usage by type from stats:', {
      rawStats: stats.byType,
      mappedUsageByType: usageByType
    });
    
    // Calculate the total from usageByType to ensure it matches the breakdown
    // This ensures the total matches the sum of individual credit types
    const totalUsage = Object.values(usageByType).reduce((sum, val) => sum + val, 0);
    
    // Calculate percentages - if we have image credits, set image to 100%
    let usagePercentByType = { template: 0, image: 0, sizechart: 0, other: 0 };
    
    if (totalUsage > 0) {
      // If we have a total but no byType breakdown, assume all credits are for images
      if (!stats.byType && stats.total) {
        usagePercentByType.image = 100;
      } else {
        // Otherwise calculate percentages normally
        Object.entries(usageByType).forEach(([type, usage]) => {
          usagePercentByType[type] = Math.round((usage / totalUsage) * 100);
        });
      }
    }
    
    // Build monthly trend data with default values
    const monthlyTrend = months.map(month => {
      // Extract month name for display
      const [year, monthNum] = month.split('-');
      const monthDate = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
      const monthName = monthDate.toLocaleString('default', { month: 'short' });
      
      // Get the current month in the same format
      const currentMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
      
      // Create monthly data with defaults
      const monthlyData = {
        month,
        monthName,
        credits: stats.byMonth?.[month] || 0,
        template: 0,
        image: 0,
        sizechart: 0,
        other: 0
      };
      
      // If we have a total but no byType breakdown, check if we can determine the type from the document structure
      if (!stats.byType && stats.total && month === currentMonth) {
        // Check if we have byType data in the monthly breakdown
        const monthlyTypeData = stats.byMonthType?.[month];
        if (monthlyTypeData) {
          // Use the type-specific data from the monthly breakdown
          monthlyData.template = monthlyTypeData.template || 0;
          monthlyData.image = monthlyTypeData.image || 0;
          monthlyData.sizechart = monthlyTypeData.sizechart || 0;
          monthlyData.other = monthlyTypeData.other || 0;
        } else {
          // For backward compatibility, assume image if we can't determine
          monthlyData.image = stats.total;
        }
        monthlyData.credits = stats.total;
      }
      
      // Use a prioritized approach to avoid double-counting
      // Only proceed to the next method if we haven't found data in the previous method
      let foundMonthlyData = false;
      
      // Method 1: Check for direct byType.*.byMonth fields in the document root
      // These fields might be named like 'byType.image.byMonth.2025-05', 'byType.sizechart.byMonth.2025-05', etc.
      for (const key in stats) {
        if (key.startsWith('byType.') && key.includes('.byMonth.')) {
          const parts = key.split('.');
          if (parts.length === 4 && parts[2] === 'byMonth' && parts[3] === month) {
            const type = parts[1]; // Extract the type (image, sizechart, etc.)
            if (type in monthlyData) {
              monthlyData[type] = stats[key] || 0;
              foundMonthlyData = true;
            }
          }
        }
      }

      // Method 2: If we haven't found monthly data in the root, check byType object
      if (!foundMonthlyData && stats.byType) {
        // Handle different data structures for byType
        // 1. If byType has nested objects with byMonth property
        if (stats.byType.template?.byMonth || stats.byType.image?.byMonth || 
            stats.byType.sizechart?.byMonth || stats.byType.other?.byMonth) {
          
          let hasData = false;
          
          if (stats.byType.template?.byMonth?.[month]) {
            monthlyData.template = stats.byType.template.byMonth[month] || 0;
            hasData = true;
          }
          
          if (stats.byType.image?.byMonth?.[month]) {
            monthlyData.image = stats.byType.image.byMonth[month] || 0;
            hasData = true;
          }
          
          if (stats.byType.sizechart?.byMonth?.[month]) {
            monthlyData.sizechart = stats.byType.sizechart.byMonth[month] || 0;
            hasData = true;
          }
          
          if (stats.byType.other?.byMonth?.[month]) {
            monthlyData.other = stats.byType.other.byMonth[month] || 0;
            hasData = true;
          }
          
          if (hasData) {
            foundMonthlyData = true;
          }
        }
        // 2. If byType has direct counts, use byMonthType for monthly breakdown
        else if (stats.byMonthType && stats.byMonthType[month]) {
          monthlyData.template = stats.byMonthType[month].template || 0;
          monthlyData.image = stats.byMonthType[month].image || 0;
          monthlyData.sizechart = stats.byMonthType[month].sizechart || 0;
          monthlyData.other = stats.byMonthType[month].other || 0;
          foundMonthlyData = true;
        }
      }
      
      // Method 3: If we still haven't found monthly data, check byShop structure
      if (!foundMonthlyData && stats.byShop) {
        // Iterate through all shops
        Object.entries(stats.byShop).forEach(([shopId, shopStats]) => {
          // Only process if we haven't found data yet
          if (!foundMonthlyData) {
            // Check for direct byType.*.byMonth fields in the shop data
            for (const key in shopStats) {
              if (key.startsWith('byType.') && key.includes('.byMonth.')) {
                const parts = key.split('.');
                if (parts.length === 4 && parts[2] === 'byMonth' && parts[3] === month) {
                  const type = parts[1]; // Extract the type (image, sizechart, etc.)
                  if (type in monthlyData) {
                    monthlyData[type] = shopStats[key] || 0;
                    foundMonthlyData = true;
                  }
                }
              }
            }
            
            // Check for byType fields in the shop data if we haven't found data yet
            if (!foundMonthlyData && shopStats.byType) {
              // For each shop, check if there's monthly data for this type
              if (shopStats.byMonthType && shopStats.byMonthType[month]) {
                monthlyData.template = shopStats.byMonthType[month].template || 0;
                monthlyData.image = shopStats.byMonthType[month].image || 0;
                monthlyData.sizechart = shopStats.byMonthType[month].sizechart || 0;
                monthlyData.other = shopStats.byMonthType[month].other || 0;
                foundMonthlyData = true;
              }
            }
          }
        });
      }
      
      return monthlyData;
    });
    
    // Build daily trend data with default values
    const dailyTrend = days.map(day => {
      // Get today's date in the same format
      const today = new Date();
      const todayFormatted = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      // Create daily data with defaults
      const dailyData = {
        date: day,
        credits: stats.byDay?.[day] || 0,
        template: 0,
        image: 0,
        sizechart: 0,
        other: 0
      };
      
      // Debug log to help troubleshoot
      console.log('Processing day:', day, 'with initial data:', JSON.stringify(dailyData));
      
      // Use a prioritized approach to avoid double-counting
      // Only proceed to the next method if we haven't found data in the previous method
      let foundDailyData = false;
      
      // Method 1: Check for direct byType.*.byDay fields in the document root
      // These fields might be named like 'byType.image.byDay.2025-05-04', 'byType.sizechart.byDay.2025-05-04', etc.
      for (const key in stats) {
        if (key.startsWith('byType.') && key.includes('.byDay.')) {
          const parts = key.split('.');
          if (parts.length === 4 && parts[2] === 'byDay' && parts[3] === day) {
            const type = parts[1]; // Extract the type (image, sizechart, etc.)
            if (type in dailyData) {
              dailyData[type] = stats[key] || 0;
              dailyData.credits += stats[key] || 0;
              foundDailyData = true;
            }
          }
        }
      }

      // Method 2: If we haven't found daily data in the root, check byType object
      if (!foundDailyData && stats.byType) {
        // Handle different data structures for byType
        // 1. If byType has nested objects with byDay property
        if (stats.byType.template?.byDay || stats.byType.image?.byDay || 
            stats.byType.sizechart?.byDay || stats.byType.other?.byDay) {
          
          let hasData = false;
          
          if (stats.byType.template?.byDay?.[day]) {
            dailyData.template = stats.byType.template.byDay[day] || 0;
            dailyData.credits += stats.byType.template.byDay[day] || 0;
            hasData = true;
          }
          
          if (stats.byType.image?.byDay?.[day]) {
            dailyData.image = stats.byType.image.byDay[day] || 0;
            dailyData.credits += stats.byType.image.byDay[day] || 0;
            hasData = true;
          }
          
          if (stats.byType.sizechart?.byDay?.[day]) {
            dailyData.sizechart = stats.byType.sizechart.byDay[day] || 0;
            dailyData.credits += stats.byType.sizechart.byDay[day] || 0;
            hasData = true;
          }
          
          if (stats.byType.other?.byDay?.[day]) {
            dailyData.other = stats.byType.other.byDay[day] || 0;
            dailyData.credits += stats.byType.other.byDay[day] || 0;
            hasData = true;
          }
          
          if (hasData) {
            foundDailyData = true;
          }
        }
        // 2. If byType has direct counts, use byDayType for daily breakdown
        else if (stats.byDayType && stats.byDayType[day]) {
          dailyData.template = stats.byDayType[day].template || 0;
          dailyData.image = stats.byDayType[day].image || 0;
          dailyData.sizechart = stats.byDayType[day].sizechart || 0;
          dailyData.other = stats.byDayType[day].other || 0;
          
          // Update credits total
          dailyData.credits = (stats.byDayType[day].template || 0) + 
                             (stats.byDayType[day].image || 0) + 
                             (stats.byDayType[day].sizechart || 0) + 
                             (stats.byDayType[day].other || 0);
          
          foundDailyData = true;
        }
      }
      
      // Method 3: If we still haven't found daily data, check byShop structure
      if (!foundDailyData && stats.byShop) {
        // Iterate through all shops
        Object.entries(stats.byShop).forEach(([shopId, shopStats]) => {
          // Only process if we haven't found data yet
          if (!foundDailyData) {
            // Check for direct byType.*.byDay fields in the shop data
            for (const key in shopStats) {
              if (key.startsWith('byType.') && key.includes('.byDay.')) {
                const parts = key.split('.');
                if (parts.length === 4 && parts[2] === 'byDay' && parts[3] === day) {
                  const type = parts[1]; // Extract the type (image, sizechart, etc.)
                  if (type in dailyData) {
                    dailyData[type] = shopStats[key] || 0;
                    dailyData.credits += shopStats[key] || 0;
                    foundDailyData = true;
                  }
                }
              }
            }
            
            // Check for byType fields in the shop data if we haven't found data yet
            if (!foundDailyData && shopStats.byType) {
              // For each shop, check if there's daily data for this type
              if (shopStats.byDayType && shopStats.byDayType[day]) {
                dailyData.template = shopStats.byDayType[day].template || 0;
                dailyData.image = shopStats.byDayType[day].image || 0;
                dailyData.sizechart = shopStats.byDayType[day].sizechart || 0;
                dailyData.other = shopStats.byDayType[day].other || 0;
                
                // Update credits total
                dailyData.credits = (shopStats.byDayType[day].template || 0) + 
                                   (shopStats.byDayType[day].image || 0) + 
                                   (shopStats.byDayType[day].sizechart || 0) + 
                                   (shopStats.byDayType[day].other || 0);
                
                foundDailyData = true;
              }
            }
          }
        });
      }
      
      // If we have a total but no byType breakdown, check if we can determine the type from the document structure
      if (dailyData.template === 0 && dailyData.image === 0 && dailyData.sizechart === 0 && dailyData.other === 0 && stats.total && day === todayFormatted) {
        // Check if we have byType data in the daily breakdown
        const dailyTypeData = stats.byDayType?.[day];
        if (dailyTypeData) {
          // Use the type-specific data from the daily breakdown
          dailyData.template = dailyTypeData.template || 0;
          dailyData.image = dailyTypeData.image || 0;
          dailyData.sizechart = dailyTypeData.sizechart || 0;
          dailyData.other = dailyTypeData.other || 0;
        } else {
          // For backward compatibility, assume image if we can't determine
          dailyData.image = stats.total;
          dailyData.credits = stats.total;
        }
      }
      
      // Return the daily data object for this day
      return dailyData;
    });
    
    // Add debug log to check final daily trend data
    console.log('Final dailyTrend data:', JSON.stringify(dailyTrend));
    
    return {
      totalUsage,
      usageByType,
      monthlyTrend,
      dailyTrend,
      usagePercentByType
    };
  } catch (error) {
    console.error('Error getting credit usage stats:', error);
    return {
      totalUsage: 0,
      usageByType: { template: 0, image: 0, sizechart: 0, other: 0 },
      monthlyTrend: [],
      dailyTrend: [],
      usagePercentByType: { template: 0, image: 0, sizechart: 0, other: 0 }
    };
  }
};

/**
 * Get detailed credit statistics
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Credit statistics
 */
export const getCreditStats = async (userId) => {
  try {
    // Get user data for basic credit info
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    if (!userData) return null;

    const credits = userData.credits || { total: 0, used: 0, monthly: 0, flex: 0 };
    
    // Get credit usage stats for detailed breakdown
    const usageStats = await getCreditUsageStats(userId, 6);
    
    // Get current date info for trends
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    
    // Calculate last month
    const lastMonthDate = new Date(now);
    lastMonthDate.setMonth(now.getMonth() - 1);
    const lastMonth = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}`;
    
    // Generate months for the last 6 months for trends
    const months = [];
    for (let i = 0; i < 6; i++) {
      const date = new Date(now);
      date.setMonth(now.getMonth() - i);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months.unshift(monthKey); // Add to beginning to maintain chronological order
    }
    
    // Generate days for the last 14 days for trends
    const days = [];
    for (let i = 0; i < 14; i++) {
      const date = new Date(now);
      date.setDate(now.getDate() - (13 - i));
      const dayKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      days.push(dayKey);
    }
    
    // Extract usage by type
    const templateCredits = usageStats.usageByType.template || 0;
    const imageCredits = usageStats.usageByType.image || 0;
    const sizechartCredits = usageStats.usageByType.sizechart || 0;
    const otherCredits = usageStats.usageByType.other || 0;
    
    // Calculate daily average from the monthly trend
    let dailyAverage = 0;
    if (usageStats.monthlyTrend && usageStats.monthlyTrend.length > 0) {
      const thisMonthData = usageStats.monthlyTrend.find(m => m.month === currentMonth);
      if (thisMonthData) {
        const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        const daysPassed = Math.min(now.getDate(), daysInMonth);
        dailyAverage = thisMonthData.credits / daysPassed;
      }
    }
    
    // Get shop breakdown from the stats
    const shopBreakdown = [];
    const statsRef = doc(db, 'credit_usage_stats', userId);
    const statsDoc = await getDoc(statsRef);
    
    if (statsDoc.exists()) {
      const stats = statsDoc.data();
      if (stats.byShop) {
        Object.entries(stats.byShop).forEach(([shopId, shopStats]) => {
          shopBreakdown.push({
            shopId,
            total: shopStats.total || 0,
            template: shopStats.byType?.template?.total || 0,
            image: shopStats.byType?.image?.total || 0,
            sizechart: shopStats.byType?.sizechart?.total || 0,
            other: shopStats.byType?.other?.total || 0
          });
        });
      }
    }
    
    // Get push metrics from user document
    const pushHistory = userData.pushHistory || { totalPushes: 0, monthlyPushes: {}, shopPushes: {} };
    const totalPushes = pushHistory.totalPushes || 0;
    const thisMonthPushes = pushHistory.monthlyPushes?.[currentMonth] || 0;
    const lastMonthPushes = pushHistory.monthlyPushes?.[lastMonth] || 0;
    
    // Get shop-specific push metrics
    const shopPushes = {};
    if (pushHistory.shopPushes) {
      Object.entries(pushHistory.shopPushes).forEach(([shopId, count]) => {
        shopPushes[shopId] = count;
      });
    }
    
    // Build monthly push trend data
    const monthlyTrend = months.map(month => {
      // Extract month name for display
      const [year, monthNum] = month.split('-');
      const monthDate = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
      const monthName = monthDate.toLocaleString('default', { month: 'short' });
      
      return {
        month,
        monthName,
        pushes: pushHistory.monthlyPushes?.[month] || 0
      };
    });
    
    // Build daily push trend data
    const dailyTrend = days.map(day => {
      // We don't have daily push data in the current structure, so we'll estimate
      // If it's today and we have pushes this month, we'll show them all on today
      const today = new Date();
      const todayFormatted = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      
      let pushes = 0;
      if (day === todayFormatted && pushHistory.monthlyPushes?.[currentMonth]) {
        pushes = pushHistory.monthlyPushes[currentMonth];
      }
      
      return {
        date: day,
        pushes
      };
    });
    
    // Calculate total used credits (from both user document and usage stats)
    const totalUsed = credits.used || 0;
    const totalUsageFromStats = usageStats.totalUsage || 0;
    
    // Use the higher value between credits.used and usageStats.totalUsage
    const effectiveTotalUsed = Math.max(totalUsed, totalUsageFromStats);
    
    // Log the values for debugging
    console.log('Credit stats calculation:', {
      'credits.used': totalUsed,
      'usageStats.totalUsage': totalUsageFromStats,
      'effectiveTotalUsed': effectiveTotalUsed
    });
    
    return {
      available: (credits.monthly || 0) + (credits.flex || 0),
      used: effectiveTotalUsed,
      monthly: credits.monthly || 0,
      flex: credits.flex || 0,
      totalUsed: effectiveTotalUsed,  // Add this for clarity
      templateCredits,
      imageCredits,
      sizechartCredits,
      otherCredits,
      totalPushes,
      thisMonthPushes,
      lastMonthPushes,
      shopPushes,
      thisMonth: usageStats.monthlyTrend.find(m => m.month === currentMonth)?.credits || totalUsageFromStats, // Use totalUsage if no monthly data
      lastMonth: usageStats.monthlyTrend.find(m => m.month === lastMonth)?.credits || 0,
      monthlyTrend: usageStats.monthlyTrend,
      dailyTrend: usageStats.dailyTrend,
      monthlyPushTrend: monthlyTrend,
      dailyPushTrend: dailyTrend,
      dailyAverage: Math.round(dailyAverage * 100) / 100,
      shopBreakdown
    };
  } catch (error) {
    console.error('Error getting credit stats:', error);
    return null;
  }
};

/**
 * Get detailed credit usage records for admin analysis
 * @param {string} userId - User ID (optional, for filtering by user)
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 * @param {number} limit - Maximum number of records to return
 * @returns {Promise<Array>} - Credit usage records
 */
export const getDetailedCreditUsage = async (userId = null, startDate = null, endDate = null, limit = 100) => {
  try {
    // Convert dates to timestamps for comparison
    const startTimestamp = startDate ? new Date(startDate) : new Date(0); // Unix epoch if not provided
    const endTimestamp = endDate ? new Date(endDate) : new Date(); // Current time if not provided
    
    // Build query to get batch documents
    let batchQuery;
    if (userId) {
      // Filter by user ID if provided
      batchQuery = query(
        collection(db, 'credit_usage_batches'),
        where('userId', '==', userId)
      );
    } else {
      // Get all batches if no user ID filter
      batchQuery = query(collection(db, 'credit_usage_batches'));
    }
    
    const batchSnapshot = await getDocs(batchQuery);
    
    // Process all batches and extract individual usage records
    let allUsages = [];
    
    batchSnapshot.forEach(doc => {
      const batch = doc.data();
      const usages = batch.usages || [];
      
      // Filter usages by date range
      const filteredUsages = usages.filter(usage => {
        // Convert Firestore timestamp to JS Date
        const usageDate = usage.timestamp.toDate();
        return usageDate >= startTimestamp && usageDate <= endTimestamp;
      });
      
      // Add batch metadata to each usage
      const processedUsages = filteredUsages.map(usage => ({
        ...usage,
        userId: batch.userId,
        batchId: doc.id
      }));
      
      allUsages = [...allUsages, ...processedUsages];
    });
    
    // Sort by timestamp (newest first) and limit results
    allUsages.sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate());
    
    // Apply limit
    return allUsages.slice(0, limit);
  } catch (error) {
    console.error('Error getting detailed credit usage:', error);
    return [];
  }
};

/**
 * Migrate existing credit usage records to the new batched format
 * This is a one-time operation to transition from the old system
 * @returns {Promise<boolean>} - Success status
 */
/**
 * Refund credits to a user
 * @param {string} userId - User ID
 * @param {number} creditsToRefund - Number of credits to refund
 * @param {Object} metadata - Additional metadata about the refund
 * @returns {Promise<boolean>} - Success status
 */
export const refundCredits = async (userId, creditsToRefund = 1, metadata = {}) => {
  try {
    console.log(`Refunding ${creditsToRefund} credits to user ${userId}`);
    const userRef = doc(db, 'users', userId);
    
    // Use a transaction to ensure credit balance consistency
    await runTransaction(db, async (transaction) => {
      const userDoc = await transaction.get(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User document not found');
      }
      
      const userData = userDoc.data();
      if (!userData.credits) {
        throw new Error('No credits found for user to refund');
      }
      
      // Prepare updates
      const updates = {
        'credits.used': increment(-creditsToRefund) // Decrement used credits
      };
      
      // Add credits back to the monthly pool first
      updates['credits.monthly'] = (userData.credits.monthly || 0) + creditsToRefund;
      
      // Also update the legacy total field for backward compatibility
      updates['credits.total'] = (userData.credits.total || 0) + creditsToRefund;
      
      // Apply the updates
      transaction.update(userRef, updates);
    });
    
    // Record the refund in the credit usage system
    await recordCreditUsage(userId, -creditsToRefund, {
      ...metadata,
      type: 'refund',
      reason: metadata.reason || 'Manual refund'
    });
    
    console.log(`Successfully refunded ${creditsToRefund} credits to user ${userId}`);
    return true;
  } catch (error) {
    console.error('Error refunding credits:', error);
    return false;
  }
};

/**
 * Refund credits for template executions
 * @param {string} userId - User ID
 * @param {Array|Object} executionMetadata - Metadata about the template executions
 * @returns {Promise<boolean>} - Success status
 */
export const refundCreditsForTemplateExecution = async (userId, executionMetadata) => {
  try {
    if (!userId) {
      console.error('User ID is required to refund template execution credits');
      return false;
    }

    if (!executionMetadata) {
      console.error('Execution metadata is required to refund template execution credits');
      return false;
    }

    // Handle both single execution metadata and arrays
    const metadataArray = Array.isArray(executionMetadata) ? executionMetadata : [executionMetadata];
    if (metadataArray.length === 0) {
      console.warn('No execution metadata provided for refund');
      return false;
    }

    // Calculate total credits to refund
    let totalCreditsToRefund = 0;
    for (const metadata of metadataArray) {
      if (metadata.totalCreditCost) {
        totalCreditsToRefund += metadata.totalCreditCost;
      }
    }

    if (totalCreditsToRefund <= 0) {
      console.warn('No credits to refund based on provided metadata');
      return false;
    }

    // Refund the credits
    await refundCredits(userId, totalCreditsToRefund, {
      type: 'template_execution_refund',
      executionIds: metadataArray.map(m => m.executionId).filter(Boolean),
      templateIds: metadataArray.map(m => m.templateId).filter(Boolean),
      reason: 'Template execution refund'
    });

    console.log(`Successfully refunded ${totalCreditsToRefund} credits to user ${userId} for ${metadataArray.length} template executions`);
    return true;
  } catch (error) {
    console.error('Error refunding credits for template executions:', error);
    return false;
  }
};

/**
 * Record a successful product push with shop-specific and aggregate metrics
 * @param {string} userId - User ID
 * @param {string} shopId - Shop ID
 * @param {string} productId - Product ID
 * @returns {Promise<boolean>} - Success status
 */
export const recordPushToShopify = async (userId, shopId, productId) => {
  try {
    // 1. Record in user's push history
    const pushRef = collection(db, 'users', userId, 'productPushes');
    await addDoc(pushRef, {
      productId,
      shopId,
      timestamp: new Date().toISOString()
    });
    
    // 2. Update shop-specific push metrics
    if (shopId) {
      const shopRef = doc(db, 'shops', shopId);
      const shopDoc = await getDoc(shopRef);
      
      if (shopDoc.exists()) {
        // Get current date for monthly and daily stats
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;
        const day = now.getDate();
        const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
        const dayKey = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
        
        await updateDoc(shopRef, {
          'metrics.totalPushes': increment(1),
          [`metrics.pushes.monthly.${monthKey}`]: increment(1),
          [`metrics.pushes.daily.${dayKey}`]: increment(1),
          'metrics.lastPushDate': new Date().toISOString()
        });
        
        console.log('Updated shop push metrics:', {
          shopId,
          monthKey,
          dayKey
        });
      }
    }
    
    // 3. Update aggregate user metrics with more detailed tracking
    const userRef = doc(db, 'users', userId);
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
    
    // Update total pushes, monthly pushes, and shop-specific pushes
    await updateDoc(userRef, {
      'pushHistory.totalPushes': increment(1),
      [`pushHistory.monthlyPushes.${monthKey}`]: increment(1),
      [`pushHistory.shopPushes.${shopId}`]: increment(1)
    });
    
    return true;
  } catch (error) {
    console.error('Error recording product push:', error);
    throw error;
  }
};

/**
 * Get user's credit status including subscription info
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Credit status object
 */
export const getUserCreditStatus = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    const userData = userDoc.data();
    
    console.log('User data for credit status:', {
      userId,
      hasSubscription: !!userData?.subscription,
      isVIP: !!userData?.isVIP,
      vipPlan: userData?.vipPlan,
      hasVipSubscription: !!userData?.vipSubscription
    });
    
    // Get user's subscription if available
    let subscription = userData?.subscription || null;
    
    // Check for VIP status
    const isVIP = userData?.isVIP || false;
    const vipPlan = userData?.vipPlan || null;
    let vipSubscription = userData?.vipSubscription || null;
    
    // If user has a VIP subscription, add it to the result
    if (vipSubscription) {
      console.log('VIP subscription found:', {
        plan: vipSubscription.plan,
        status: vipSubscription.status,
        startDate: vipSubscription.startDate,
        endDate: vipSubscription.endDate,
        monthlyCredits: vipSubscription.monthlyCredits
      });
    }
    
    const result = {
      credits: userData?.credits || { total: 0, used: 0, monthly: 0, flex: 0 },
      pushHistory: userData?.pushHistory || { totalPushes: 0 },
      subscription: subscription,
      vipSubscription: vipSubscription,
      isVIP,
      vipPlan
    };
    
    console.log('Returning credit status:', result);
    return result;
  } catch (error) {
    console.error('Error getting user credit status:', error);
    throw error;
  }
};

export const migrateCreditUsageRecords = async () => {
  try {
    // Get all existing credit usage records
    const usageQuery = query(collection(db, 'credit_usage'));
    const usageSnapshot = await getDocs(usageQuery);
    
    if (usageSnapshot.empty) {
      console.log('No credit usage records to migrate');
      return true;
    }
    
    // Group records by user and month
    const recordsByUserMonth = {};
    
    usageSnapshot.forEach(doc => {
      const usage = doc.data();
      const userId = usage.userId;
      
      if (!userId) {
        console.warn('Skipping record without userId:', doc.id);
        return;
      }
      
      // Get timestamp
      const timestamp = usage.timestamp?.toDate() || new Date();
      const year = timestamp.getFullYear();
      const month = timestamp.getMonth() + 1;
      const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
      const userMonthKey = `${userId}_${monthKey}`;
      
      // Initialize group if needed
      if (!recordsByUserMonth[userMonthKey]) {
        recordsByUserMonth[userMonthKey] = {
          userId,
          monthKey,
          usages: []
        };
      }
      
      // Add usage record to group
      recordsByUserMonth[userMonthKey].usages.push({
        timestamp: usage.timestamp || Timestamp.now(),
        credits: usage.credits || 1,
        usageType: usage.usageType || 'other',
        shopId: usage.shopId || null,
        templateId: usage.templateId || null,
        modelId: usage.modelId || null,
        productId: usage.productId || null,
        type: usage.type || null
      });
    });
    
    // Create batch documents for each group
    const batch = writeBatch(db);
    let batchCount = 0;
    let totalRecords = 0;
    
    for (const [userMonthKey, group] of Object.entries(recordsByUserMonth)) {
      // Split into batches of BATCH_SIZE
      const { userId, monthKey, usages } = group;
      
      for (let i = 0; i < usages.length; i += BATCH_SIZE) {
        const batchUsages = usages.slice(i, i + BATCH_SIZE);
        const batchId = i === 0 ? userMonthKey : `${userMonthKey}_${i}`;
        const batchRef = doc(db, 'credit_usage_batches', batchId);
        
        batch.set(batchRef, {
          userId,
          monthKey,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          usages: batchUsages
        });
        
        totalRecords += batchUsages.length;
        batchCount++;
        
        // Commit batch if it's getting large (Firestore limit is 500 operations per batch)
        if (batchCount >= 400) {
          await batch.commit();
          console.log(`Committed batch with ${batchCount} documents (${totalRecords} records)`);
          batchCount = 0;
          batch = writeBatch(db);
        }
      }
    }
    
    // Commit any remaining operations
    if (batchCount > 0) {
      await batch.commit();
      console.log(`Committed final batch with ${batchCount} documents (${totalRecords} records)`);
    }
    
    console.log(`Migration complete: ${totalRecords} records migrated to ${Object.keys(recordsByUserMonth).length} batch documents`);
    return true;
  } catch (error) {
    console.error('Error migrating credit usage records:', error);
    throw error;
  }
};
